// Validation types
export interface ValidationResult {
  isValid: boolean;
  message: string;
  field: string;
}

// Validation functions
export const validatePhoneNumber = (phone: string): ValidationResult => {
  const cleanPhone = (phone ?? "").toString().replace(/\D/g, "");

  if (!cleanPhone) {
    return {
      isValid: false,
      message: "Phone number is required",
      field: "phone",
    };
  }

  if (cleanPhone.length < 10) {
    return {
      isValid: false,
      message: `Phone number must be at least 10 digits. Current: ${cleanPhone.length} digits`,
      field: "phone",
    };
  }

  if (cleanPhone.length > 15) {
    return {
      isValid: false,
      message: `Phone number too long. Maximum 15 digits allowed. Current: ${cleanPhone.length} digits`,
      field: "phone",
    };
  }

  return {
    isValid: true,
    message: "Valid phone number",
    field: "phone",
  };
};

export const validateEmail = (email: string): ValidationResult => {
  if (!email) {
    return {
      isValid: true,
      message: "Email is optional",
      field: "email",
    };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return {
      isValid: false,
      message: "Invalid email format",
      field: "email",
    };
  }

  return {
    isValid: true,
    message: "Valid email",
    field: "email",
  };
};

// Main validation function
export const validateRow = (
  row: Record<string, any>,
  headers: string[],
): ValidationResult[] => {
  const validations: ValidationResult[] = [];

  const contactHeader = headers.find((header) => {
    const lower = header.toLowerCase();
    return (
      lower === "contact_number_one" ||
      lower.includes("contact") ||
      lower.includes("mobile") ||
      lower.includes("phone")
    );
  });

  if (contactHeader) {
    validations.push(validatePhoneNumber(row[contactHeader]?.toString() || ""));
  }

  return validations;
};

// Simple boolean validation for stats (only checks contact_number_one)
export const validateRowForStats = (
  row: Record<string, any>,
  headers: string[],
): boolean => {
  const contactHeader = headers.find((header) => {
    const lower = header.toLowerCase();
    return (
      lower === "contact_number_one" ||
      lower.includes("contact") ||
      lower.includes("mobile") ||
      lower.includes("phone")
    );
  });

  if (!contactHeader) return true;
  const phoneResult = validatePhoneNumber(row[contactHeader]?.toString() || "");
  return phoneResult.isValid;
};

export const validateRowByMappings = (
  row: Record<string, any>,
  mappings: {
    header: string;
    selectedMapping: string;
    isMapped?: boolean;
    isChecked?: boolean;
  }[],
): ValidationResult[] => {
  const validations: ValidationResult[] = [];

  const contactHeader = mappings.find((m) => {
    const isMappedAndChecked = m.isMapped !== false && m.isChecked !== false;
    if (!isMappedAndChecked || !m.selectedMapping) return false;
    const selected = m.selectedMapping.toLowerCase();
    return selected === "contact_number_one";
  })?.header;

  if (!contactHeader) {
    validations.push({
      isValid: false,
      message: "Contact Number One mapping is required",
      field: "contact_number_one",
    });
  } else {
    validations.push(validatePhoneNumber(row[contactHeader]?.toString() || ""));
  }

  return validations;
};

export const validateRowForStatsByMappings = (
  row: Record<string, any>,
  mappings: {
    header: string;
    selectedMapping: string;
    isMapped?: boolean;
    isChecked?: boolean;
  }[],
): boolean => {
  const contactHeader = mappings.find((m) => {
    const isMappedAndChecked = m.isMapped !== false && m.isChecked !== false;
    if (!isMappedAndChecked || !m.selectedMapping) return false;
    const selected = m.selectedMapping.toLowerCase();
    return selected === "contact_number_one";
  })?.header;

  if (!contactHeader) return false;

  const phoneOk = validatePhoneNumber(
    row[contactHeader]?.toString() || "",
  ).isValid;
  return phoneOk;
};
